/**
 * @fileoverview Sales CRM target API service.
 * @description Calls the backend's target endpoints (Confluence 3.5.11.12-.14): the open periods of
 * one type (GET /sales/targets/periods), the users the caller may give a target to (GET
 * /sales/targets/eligible-assignees, every page at once) and Assign Target (POST /sales/targets),
 * which takes one all-or-nothing batch of one to a hundred backlog prospects for one assignee and
 * sends the caller's `Idempotency-Key`. Separate from targets.service.ts, whose per-tenant Target calls
 * serve the mock Tenant/Target model the other sales pages still read.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { readAllItems } from '@/features/sales/services/prospects.service'
import { httpClient } from '@/shared/api/http-client'

import type {
  AssignTargetBatchRequest,
  AssignTargetBatchResult,
  EligibleTargetAssignee,
  SalesTargetPeriods,
  SalesTargetPeriodType,
} from '@/features/sales/types/sales-target.types'

/**
 * @description Fetches the periods of one type on the April to March financial year that have not
 * ended, for the current and the next financial year, earliest first.
 * @param {SalesTargetPeriodType} type - The period type.
 * @returns {Promise<SalesTargetPeriods>} The open periods and today's date in India.
 */
export async function getTargetPeriods(type: SalesTargetPeriodType): Promise<SalesTargetPeriods> {
  const { data } = await httpClient.get<SalesTargetPeriods>('/sales/targets/periods', {
    params: { type },
  })
  return data
}

/**
 * @description Fetches every Sales Manager or Executive the caller may give a target to (never a
 * Sales Head; a Manager sees only the Executives in their own downline).
 * @returns {Promise<EligibleTargetAssignee[]>} The choices, ordered by display name.
 */
export function getEligibleTargetAssignees(): Promise<EligibleTargetAssignee[]> {
  return readAllItems<EligibleTargetAssignee>('/sales/targets/eligible-assignees', {})
}

/**
 * @description Assigns one target to each prospect of the batch for one assignee, all or nothing;
 * the assignee also becomes each prospect's owner.
 * @param {AssignTargetBatchRequest} request - The name, period, priority, assignee and prospects.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<AssignTargetBatchResult>} The batch and each prospect's new target.
 */
export async function assignTargetBatch(
  request: AssignTargetBatchRequest,
  idempotencyKey: string,
): Promise<AssignTargetBatchResult> {
  const { data } = await httpClient.post<AssignTargetBatchResult>('/sales/targets', request, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return data
}
